import styled, { css } from 'styled-components';

import { Text } from '../../components';
import { COLORS, MOBILE, SPACE } from '../../theme/tokens';

/** The Secured layout's height: room for its words and, beside them, the faded contract. */
const SECURED_MIN_PX = 240;
/** The panel's own width from which the faded contract shows; under it there is no room beside the words. */
const SECURED_ART_FROM_PX = 600;
/** The room the faded contract takes on the right (its 300px drawing and a gap), kept free of words. */
const SECURED_ART_ROOM_PX = 324;
/** The faded contract's width on a narrow panel, where it sits above the words. */
const SECURED_ART_NARROW_PX = 240;

/** The panel's content: a size container, so the Secured layout fits the panel's width, not the window's.
 *  A column, so on a narrow panel the faded contract can move above the words. */
export const Body = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  container-type: inline-size;
`;

/**
 * Settings > Secure your brand, inside its panel: one column (status line, explanation, steps,
 * actions, the line under them). The chrome around the panel (BackHeader, the content cap) comes from
 * the Settings section dispatcher. Secured, on a panel wide enough for its picture: taller, its words
 * centred down the left, clear of the faded contract on the right.
 */
export const Stack = styled.div<{ $secured?: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};

  ${(p) =>
    p.$secured
      ? css`
          @container (min-width: ${SECURED_ART_FROM_PX}px) {
            justify-content: center;
            min-height: ${SECURED_MIN_PX}px;
            padding-right: ${SECURED_ART_ROOM_PX}px;
          }
        `
      : null}
`;

/** The faded contract, on the right of the Secured layout and centred down it. On a narrow panel (a
 *  phone) there is no room beside the words, so it sits above them, centred and a little smaller, its
 *  faded bottom running into the status line. */
export const SecuredArt = styled.div`
  position: absolute;
  top: 50%;
  right: ${SPACE[0]};
  transform: translateY(-50%);
  pointer-events: none;

  @container (max-width: ${SECURED_ART_FROM_PX - 1}px) {
    position: static;
    order: -1;
    align-self: center;
    max-width: 100%;
    transform: none;

    & > svg {
      width: ${SECURED_ART_NARROW_PX}px;
      max-width: 100%;
    }
  }
`;

/** The status icon and its label. */
export const StatusLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
`;

/** "Not secured" / "Document sent" / "Secured". */
export const StatusLabel = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: ${COLORS.textStrong};
`;

/** The explanation under the status line. */
export const Intro = styled(Text)`
  margin: ${SPACE[0]};
  max-width: 640px;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/** How the previous contract ended (Not secured only). */
export const Note = styled(Text)`
  margin: ${SPACE[0]};
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/** The state's buttons; stacked full width on a phone. */
export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE[12]};

  ${MOBILE} {
    flex-direction: column;

    & > * {
      width: 100%;
    }
  }
`;

/** Why the buttons are disabled, right under them. */
export const Blocked = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textFaint};
`;
